/**
 * The local folder in the desktop app. There the folder is kept by Windows (desktop/folder.cjs)
 * instead of the browser, so it is never lost between visits and never asks again. These
 * classes give it the shape of the browser's folder handles, so the rest of the app reads and
 * writes it exactly as it does a folder picked in the browser.
 *
 * Picking, listing and writing go through the bridge. A file's bytes do not: the desktop
 * app's own server streams them (desktopMediaUrl), so media is shown from an address and a
 * file needed whole is fetched from there.
 */

export type DesktopResult<T> = { ok: true; value: T } | { ok: false; name: string; message: string };

export interface DesktopFolderInfo { name: string; path: string }
/** An entry of a folder listing; files come with their size and date. */
export interface DesktopEntry { name: string; kind: 'file' | 'directory'; size?: number; mtimeMs?: number }
/** A file found in the folder: its path inside the folder, its size and its date. */
export interface DesktopLocated { rel: string; size: number; mtimeMs: number }

/** What the desktop app puts on window (desktop/preload.cjs). */
export interface NoodleDesktop {
    platform: string;
    /** The installed app's version (desktop/package.json, the installer's number); missing or null in an older shell. */
    version?: string | null;
    /**
     * Where the app's own server streams the folder's files from: its address with this run's
     * id in it. Null or missing when the app is not served by the built-in server (the dev
     * server, the live site); files then come through the bridge.
     */
    mediaBase?: string | null;
    /**
     * A picture of what the window shows right now in `rect` (CSS pixels of the page; the whole
     * page when left out), scaled down to at most `maxWidth` pixels wide, as a JPEG data URL.
     * Taken by the shell outside the page, so it costs the page nothing.
     */
    capturePage?: (rect?: { x: number; y: number; width: number; height: number }, maxWidth?: number) => Promise<DesktopResult<string>>;
    /**
     * Tells the shell how the page saves its unsaved work. Before the window closes or reloads
     * the shell calls `flush` and waits for what it returns, a few seconds at most. Returns what
     * takes `flush` back. Missing in an older shell.
     */
    onBeforeClose?: (flush: () => Promise<void>) => () => void;
    /**
     * The API keys saved in Settings, kept on this computer encrypted by Windows. `load` answers
     * at once; `available` is false where Windows cannot encrypt them (then none are kept).
     * Missing in an older shell.
     */
    keys?: {
        load: () => { available: boolean; keys: Record<string, string> };
        save: (name: string, value: string) => Promise<DesktopResult<null>>;
        remove: (name: string) => Promise<DesktopResult<null>>;
    };
    folder: {
        get: (account: string) => Promise<DesktopResult<DesktopFolderInfo | null>>;
        pick: (account: string) => Promise<DesktopResult<DesktopFolderInfo | null>>;
        clear: (account: string) => Promise<DesktopResult<null>>;
        stat: (account: string, rel: string) => Promise<DesktopResult<{ kind: 'file' | 'directory'; size: number; mtimeMs: number }>>;
        /** The first of `rels` that is a file with something in it, or null. One call instead of a walk and a read. Missing in an older shell. */
        locate?: (account: string, rels: string[]) => Promise<DesktopResult<DesktopLocated | null>>;
        list: (account: string, rel: string) => Promise<DesktopResult<DesktopEntry[]>>;
        mkdir: (account: string, rel: string) => Promise<DesktopResult<null>>;
        read: (account: string, rel: string) => Promise<DesktopResult<{ data: Uint8Array; size: number; mtimeMs: number }>>;
        write: (account: string, rel: string, data: Uint8Array) => Promise<DesktopResult<null>>;
        /** Moves a file or a folder inside the folder, on disk, without copying it; a name already taken is an error. Missing in an older shell. */
        move?: (account: string, from: string, to: string) => Promise<DesktopResult<null>>;
        remove: (account: string, rel: string, recursive: boolean) => Promise<DesktopResult<null>>;
    };
}

/** The desktop app's bridge, or null in a browser. */
export const desktopApp = (): NoodleDesktop | null =>
    (typeof window !== 'undefined' && (window as unknown as { noodleDesktop?: NoodleDesktop }).noodleDesktop) || null;

/** The account a folder belongs to; a signed-out session has its own. */
export const folderAccount = (userId?: string): string => userId || 'default';

/** The bridge's media address without a trailing slash, or null without the built-in server. */
const mediaBaseOf = (bridge: NoodleDesktop | null): string | null => {
    const base = bridge?.mediaBase;
    return typeof base === 'string' && base ? base.replace(/\/+$/, '') : null;
};

/**
 * Where a file of the local folder is streamed from in the desktop app: an address of the
 * app's own server, so a picture or a video is read from disk as an element needs it (a video
 * only the parts it plays) and nothing is copied through the bridge. `version` (size and
 * date) gives a changed file another address. Null without the built-in server.
 *
 * The address holds this run's id and stops working when the app closes. It is for showing
 * media in this session only: never save it in a workspace or send it anywhere (see
 * isLocalMediaUrl). It opens nothing by itself: the server also wants a secret that only the
 * app's own browser holds and no page can read.
 */
export const desktopMediaUrl = (bridge: NoodleDesktop | null, account: string, rel: string, version?: string): string | null => {
    const base = mediaBaseOf(bridge);
    if (!base) return null;
    const path = [account, ...rel.split('/').filter(Boolean)].map(encodeURIComponent).join('/');
    return `${base}/${path}${version ? `?v=${encodeURIComponent(version)}` : ''}`;
};

const LOCAL_MEDIA_URL = /^https?:\/\/(127\.0\.0\.1|localhost)(:\d+)?\/__folder\//i;

/**
 * Whether an address is one the desktop app streams a folder file from, in this run or an
 * earlier one. Such an address dies with the app that made it, so code that saves anything
 * (node data, history, the workspace file, the cloud) must drop it, as it drops blob: URLs.
 * Deliberately not a type guard: callers pass `string`, and a guard would narrow the other
 * branch to `never`.
 */
export const isLocalMediaUrl = (url: unknown): boolean => typeof url === 'string' && LOCAL_MEDIA_URL.test(url);

/**
 * A text with the run's id taken out of every media-route address in it, wherever it sits (in
 * the middle of a longer string too). For text that is about to be written to disk or sent away.
 */
export const withoutLocalMediaSecrets = (text: string): string =>
    text.replace(/(https?:\/\/(?:127\.0\.0\.1|localhost)(?::\d+)?\/__folder\/)[^/"'\\\s?#]+/gi, '$1-');

/** Whether such an address belongs to the app as it runs now, so it can be loaded. */
export const isLiveLocalMediaUrl = (url: unknown): boolean => {
    const base = mediaBaseOf(desktopApp());
    return !!base && typeof url === 'string' && url.startsWith(`${base}/`);
};

const unwrap = <T>(result: DesktopResult<T>): T => {
    if (result.ok) return result.value;
    throw new DOMException(result.message, result.name);
};

const join = (dir: string, name: string) => (dir ? `${dir}/${name}` : name);

/** A blob's bytes (FileReader where Blob has no arrayBuffer). */
const bytesOf = (blob: Blob): Promise<ArrayBuffer> => (typeof blob.arrayBuffer === 'function'
    ? blob.arrayBuffer()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
    }));

const checkName = (name: string) => {
    if (!name || name === '.' || name === '..' || /[\\/]/.test(name)) throw new TypeError(`Name is not allowed: ${name}`);
};

const MIME_TYPES: Record<string, string> = {
    png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif', svg: 'image/svg+xml', avif: 'image/avif',
    mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime', m4v: 'video/mp4',
    mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg', m4a: 'audio/mp4', aac: 'audio/aac', flac: 'audio/flac',
    json: 'application/json', txt: 'text/plain', md: 'text/markdown', pdf: 'application/pdf', zip: 'application/zip',
};
/** The type a browser gives a file of that name. */
export const mimeTypeFor = (name: string): string => MIME_TYPES[name.split('.').pop()?.toLowerCase() || ''] || '';

type Handle = DesktopDirectoryHandle | DesktopFileHandle;

abstract class DesktopHandle {
    constructor(
        protected readonly bridge: NoodleDesktop,
        readonly account: string,
        /** Path inside the folder, with / between names; '' is the folder itself. */
        readonly path: string,
        readonly name: string,
    ) {}
    abstract readonly kind: 'file' | 'directory';

    async isSameEntry(other: unknown): Promise<boolean> {
        const that = other as Partial<DesktopHandle> | null;
        return !!that && that.kind === this.kind && that.account === this.account && that.path === this.path;
    }
    // The desktop app owns the folder: access is never in question
    async queryPermission(): Promise<PermissionState> { return 'granted'; }
    async requestPermission(): Promise<PermissionState> { return 'granted'; }

    /**
     * Moves this file or folder into `parent` under `name`, on disk, without copying anything
     * through the page (a browser's handles cannot). A name already taken is an error. This
     * handle names the old place afterwards and must not be used again.
     */
    async moveTo(parent: DesktopDirectoryHandle, name: string): Promise<void> {
        checkName(name);
        if (!this.bridge.folder.move) throw new DOMException('This version of the app cannot move files.', 'NotSupportedError');
        unwrap(await this.bridge.folder.move(this.account, this.path, join(parent.path, name)));
    }
}

/** When the file a response carries was last changed: the route says so in its ETag ("size-milliseconds"). */
const modifiedOf = (response: Response): number => {
    const stamp = /^"\d+-(\d+)"$/.exec(response.headers.get('etag') || '');
    if (stamp) return Number(stamp[1]);
    const dated = Date.parse(response.headers.get('last-modified') || '');
    return Number.isFinite(dated) ? dated : Date.now();
};

export class DesktopFileHandle extends DesktopHandle {
    readonly kind = 'file' as const;

    constructor(
        bridge: NoodleDesktop,
        account: string,
        path: string,
        name: string,
        /** Size and date when a listing or a lookup already gave them. */
        private readonly known?: { size: number; mtimeMs: number },
    ) {
        super(bridge, account, path, name);
    }

    /** The file's size, date and type without reading it (getFile reads all of it). */
    async info(): Promise<{ size: number; lastModified: number; type: string }> {
        let known = this.known;
        if (!known) {
            const found = unwrap(await this.bridge.folder.stat(this.account, this.path));
            if (found.kind !== 'file') throw new DOMException(`"${this.name}" is a folder, not a file.`, 'TypeMismatchError');
            known = found;
        }
        return { size: known.size, lastModified: Math.round(known.mtimeMs), type: mimeTypeFor(this.name) };
    }

    /** The address the file is streamed from (see desktopMediaUrl); null without the built-in server. */
    url(version?: string): string | null {
        return desktopMediaUrl(this.bridge, this.account, this.path, version);
    }

    async getFile(): Promise<File> {
        const url = this.url();
        if (url && typeof fetch === 'function') {
            // From the app's own server: the bytes go from disk into the browser's blob store
            // without crossing the bridge or this page's main thread
            try {
                const response = await fetch(url, { cache: 'no-store' });
                if (response.ok) {
                    const lastModified = modifiedOf(response);
                    return new File([await response.blob()], this.name, { type: mimeTypeFor(this.name), lastModified });
                }
            } catch { /* the bridge answers below */ }
        }
        // Without the server, and for what it does not send (an empty file, one that is gone): the bridge, which also says what is wrong
        const { data, mtimeMs } = unwrap(await this.bridge.folder.read(this.account, this.path));
        return new File([data as BlobPart], this.name, { type: mimeTypeFor(this.name), lastModified: Math.round(mtimeMs) });
    }

    async createWritable(): Promise<DesktopWritable> {
        return new DesktopWritable(this.bridge, this.account, this.path);
    }
}

/** Collects what is written and replaces the file with it on close, as a browser's writable does. */
export class DesktopWritable {
    private parts: BlobPart[] = [];
    private closed = false;
    constructor(private readonly bridge: NoodleDesktop, private readonly account: string, private readonly path: string) {}

    async write(data: BlobPart | { type: 'write'; data: BlobPart; position?: number } | { type: string }): Promise<void> {
        if (this.closed) throw new TypeError('The file is already closed.');
        if (data && typeof data === 'object' && 'type' in data && !(data instanceof Blob)) {
            const command = data as { type: string; data?: BlobPart; position?: number };
            if (command.type !== 'write' || command.position !== undefined) throw new DOMException(`"${command.type}" is not supported here.`, 'NotSupportedError');
            if (command.data !== undefined) this.parts.push(command.data);
            return;
        }
        this.parts.push(data as BlobPart);
    }

    async close(): Promise<void> {
        if (this.closed) return;
        this.closed = true;
        const bytes = new Uint8Array(await bytesOf(new Blob(this.parts)));
        this.parts = [];
        unwrap(await this.bridge.folder.write(this.account, this.path, bytes));
    }

    async abort(): Promise<void> {
        this.closed = true;
        this.parts = [];
    }
}

export class DesktopDirectoryHandle extends DesktopHandle {
    readonly kind = 'directory' as const;

    private async statOf(rel: string) {
        const result = await this.bridge.folder.stat(this.account, rel);
        if (!result.ok && result.name === 'NotFoundError') return null;
        return unwrap(result);
    }

    async getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<DesktopDirectoryHandle> {
        checkName(name);
        const rel = join(this.path, name);
        const found = await this.statOf(rel);
        if (!found) {
            if (!options?.create) throw new DOMException(`"${name}" was not found.`, 'NotFoundError');
            unwrap(await this.bridge.folder.mkdir(this.account, rel));
        } else if (found.kind !== 'directory') {
            throw new DOMException(`"${name}" is a file, not a folder.`, 'TypeMismatchError');
        }
        return new DesktopDirectoryHandle(this.bridge, this.account, rel, name);
    }

    async getFileHandle(name: string, options?: { create?: boolean }): Promise<DesktopFileHandle> {
        checkName(name);
        const rel = join(this.path, name);
        const found = await this.statOf(rel);
        if (!found) {
            if (!options?.create) throw new DOMException(`"${name}" was not found.`, 'NotFoundError');
            unwrap(await this.bridge.folder.write(this.account, rel, new Uint8Array(0)));
        } else if (found.kind !== 'file') {
            throw new DOMException(`"${name}" is a folder, not a file.`, 'TypeMismatchError');
        }
        // Finding the file told its size and date: the handle can say them without another call
        return new DesktopFileHandle(this.bridge, this.account, rel, name, found || undefined);
    }

    async removeEntry(name: string, options?: { recursive?: boolean }): Promise<void> {
        checkName(name);
        unwrap(await this.bridge.folder.remove(this.account, join(this.path, name), !!options?.recursive));
    }

    async *entries(): AsyncGenerator<[string, Handle]> {
        const list = unwrap(await this.bridge.folder.list(this.account, this.path));
        for (const entry of list) {
            const rel = join(this.path, entry.name);
            // The listing gave each file's size and date: the handle can tell them without another call
            const known = typeof entry.size === 'number' && typeof entry.mtimeMs === 'number' ? { size: entry.size, mtimeMs: entry.mtimeMs } : undefined;
            yield [entry.name, entry.kind === 'directory'
                ? new DesktopDirectoryHandle(this.bridge, this.account, rel, entry.name)
                : new DesktopFileHandle(this.bridge, this.account, rel, entry.name, known)];
        }
    }

    async *values(): AsyncGenerator<Handle> {
        for await (const [, handle] of this.entries()) yield handle;
    }

    async *keys(): AsyncGenerator<string> {
        for await (const [name] of this.entries()) yield name;
    }

    [Symbol.asyncIterator]() { return this.entries(); }

    /** The names leading from this folder to `other`, or null when it is not inside. */
    async resolve(other: unknown): Promise<string[] | null> {
        const that = other as Partial<DesktopHandle> | null;
        if (!that || that.account !== this.account || typeof that.path !== 'string') return null;
        if (that.path === this.path) return [];
        const prefix = this.path ? `${this.path}/` : '';
        return that.path.startsWith(prefix) ? that.path.slice(prefix.length).split('/') : null;
    }
}

const rootHandle = (bridge: NoodleDesktop, account: string, info: DesktopFolderInfo | null): FileSystemDirectoryHandle | null =>
    (info ? new DesktopDirectoryHandle(bridge, account, '', info.name) as unknown as FileSystemDirectoryHandle : null);

/** The account's folder as the app chose it before, or null. */
export const desktopFolder = async (bridge: NoodleDesktop, account: string): Promise<FileSystemDirectoryHandle | null> =>
    rootHandle(bridge, account, unwrap(await bridge.folder.get(account)));

/** Asks Windows for a folder and remembers it; null when the dialog is cancelled. */
export const pickDesktopFolder = async (bridge: NoodleDesktop, account: string): Promise<FileSystemDirectoryHandle | null> =>
    rootHandle(bridge, account, unwrap(await bridge.folder.pick(account)));

export const forgetDesktopFolder = async (bridge: NoodleDesktop, account: string): Promise<void> => {
    unwrap(await bridge.folder.clear(account));
};
